<script lang="ts">
  import { Pencil, Sun, Moon, Home as HomeIcon, HelpCircle, FileText } from "lucide-svelte";
  import { summarizeBuckets, type BucketSummary } from "./bucketize";
  import { computeDefaultCard, type DefaultCardResult } from "./defaultCard";
  import { loadFeedback, recordFeedback, type BenefitFeedback } from "./feedback";
  import type { Profile, CustomCard } from "./profile";
  import type { WalletBenefit, WalletCard } from "../wallet/types";
  import Button from "./ui/Button.svelte";
  import Hero from "./Hero.svelte";
  import BucketSection from "./BucketSection.svelte";
  import CardsManager from "./CardsManager.svelte";
  import Logo from "./Logo.svelte";
  import { toPng } from "html-to-image";
  import ShareableCard from "./ShareableCard.svelte";
  import { toast } from "./toast";
  import CardDetail from "./CardDetail.svelte";
  import HelpSheet from "./HelpSheet.svelte";
  import MyReports from "./MyReports.svelte";

  export let profile: Profile;
  export let selectedCards: WalletCard[];
  export let allBenefits: WalletBenefit[];
  export let allCatalog: WalletCard[] = [];
  export let customCards: CustomCard[] = [];
  export let theme: "light" | "dark" = "dark";
  export let onEditProfile: () => void;
  export let onProfileChange: (p: Profile) => void = () => {};
  export let onCardsChange: (next: { selectedIds: string[]; customCards: CustomCard[] }) => void = () => {};
  export let onToggleTheme: () => void = () => {};
  export let onGoHome: () => void = () => {};

  let feedbackMap: Record<string, BenefitFeedback> = loadFeedback();
  let pinPickerOpen = false;
  let shareNode: HTMLDivElement | null = null;
  let exporting = false;
  let expanded: Record<string, boolean> = {};
  let pendingExpanded: Record<string, boolean> = {};
  let detailCard: WalletCard | null = null;
  let helpOpen = false;
  let reportsOpen = false;

  $: ownedBenefits = allBenefits.filter(b => selectedCards.some(c => c.id === b.cardId));
  $: defaultCardResult = computeDefaultCard(selectedCards, ownedBenefits, profile) as DefaultCardResult;
  $: buckets = summarizeBuckets(ownedBenefits, profile) as BucketSummary[];
  $: latestReview = allBenefits
    .map(b => b.lastReviewedAt)
    .filter(Boolean)
    .sort()
    .pop() || "";

  function toggle(id: string) {
    expanded = { ...expanded, [id]: !expanded[id] };
  }
  function togglePending(id: string) {
    pendingExpanded = { ...pendingExpanded, [id]: !pendingExpanded[id] };
  }

  function handleFeedback(benefitId: string, kind: "used" | "rule_wrong") {
    const had = !!feedbackMap[benefitId];
    feedbackMap = recordFeedback(benefitId, kind);
    if (!had) {
      if (kind === "used") {
        toast.success("已记下：你用过这条");
      } else {
        toast.warn("已记下：规则不对", {
          description: "我们会按反馈频次复核这条权益。"
        });
      }
    } else {
      toast.info("已撤回反馈");
    }
  }

  function handlePin(cardId: string) {
    onProfileChange({ ...profile, pinnedDefaultCardId: cardId });
    pinPickerOpen = false;
    const card = selectedCards.find(c => c.id === cardId);
    toast.success(`已置顶：${card?.shortName || cardId}`, {
      description: "首页会一直推这张，直到你撤回。"
    });
  }
  function handleUnpin() {
    onProfileChange({ ...profile, pinnedDefaultCardId: undefined });
    toast.info("已恢复算法选默认卡");
  }

  function isIOSSafari(): boolean {
    if (typeof navigator === "undefined") return false;
    const ua = navigator.userAgent;
    const iOS = /iPad|iPhone|iPod/.test(ua);
    const webkit = /WebKit/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
    return iOS && webkit;
  }

  async function exportAsImage() {
    if (!shareNode || exporting) return;
    exporting = true;
    try {
      const dataUrl = await toPng(shareNode, {
        pixelRatio: 2,
        cacheBust: true,
        backgroundColor: "#0f1320"
      });
      const filename = `mofaxiaoka-${defaultCardResult.card?.shortName || "default-card"}-${new Date()
        .toISOString()
        .slice(0, 10)}.png`;

      if (isIOSSafari()) {
        // iOS Safari 忽略 download 属性。新标签打开图片，提示长按保存。
        const w = window.open();
        if (w) {
          w.document.write(
            `<title>${filename}</title><body style="margin:0;background:#0f1320;display:flex;align-items:center;justify-content:center;min-height:100vh;"><img src="${dataUrl}" alt="${filename}" style="max-width:100%;max-height:100vh;"/></body>`
          );
          w.document.close();
          toast.info("图片已打开新页面", {
            description: "iOS Safari 请长按图片选择「存储到照片」。"
          });
        } else {
          toast.warn("浏览器拦截了弹窗", {
            description: "请允许弹窗后再点一次「存为壁纸」。"
          });
        }
      } else {
        const link = document.createElement("a");
        link.download = filename;
        link.href = dataUrl;
        document.body.appendChild(link);
        link.click();
        link.remove();
        toast.success("壁纸已下载", { description: "存到锁屏，不用再打开 App。" });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error("导出失败", { description: msg });
    } finally {
      exporting = false;
    }
  }
</script>

<div class="relative mx-auto max-w-2xl px-5 pb-12 pt-6">
  <header class="anim-fade-in-up mb-6 flex items-center justify-between">
    <button
      type="button"
      class="focus-amber flex items-center gap-2 rounded-md transition-opacity hover:opacity-80"
      on:click={onGoHome}
      aria-label="回到主页"
    >
      <Logo size={26} />
      <div class="flex flex-col items-start leading-tight">
        <span class="font-display text-base text-foreground">魔法小卡</span>
        <span class="text-[10px] tracking-widest text-muted-foreground">MAGIC CARD · BETA</span>
      </div>
    </button>
    <div class="flex items-center gap-1.5">
      <button
        type="button"
        class="focus-amber inline-flex size-9 items-center justify-center rounded-full border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="你的反馈与报告"
        on:click={() => (reportsOpen = true)}
      >
        <FileText class="size-4" />
      </button>
      <button
        type="button"
        class="focus-amber inline-flex size-9 items-center justify-center rounded-full border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="怎么工作的"
        on:click={() => (helpOpen = true)}
      >
        <HelpCircle class="size-4" />
      </button>
      <!-- Home 入口已经在左上 Logo 上；mobile 隐藏避免拥挤 -->
      <button
        type="button"
        class="focus-amber hidden size-9 items-center justify-center rounded-full border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground sm:inline-flex"
        aria-label="回到主页"
        on:click={onGoHome}
      >
        <HomeIcon class="size-4" />
      </button>
      <button
        type="button"
        class="focus-amber inline-flex size-9 items-center justify-center rounded-full border border-input bg-background text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
        aria-label="切换深色 / 浅色"
        on:click={onToggleTheme}
      >
        {#if theme === "dark"}
          <Sun class="size-4" />
        {:else}
          <Moon class="size-4" />
        {/if}
      </button>
      <Button variant="ghost" size="sm" on:click={onEditProfile} class="!px-3 sm:!px-4">
        <Pencil class="size-3.5 sm:mr-1.5" />
        <span class="hidden sm:inline">改画像</span>
      </Button>
    </div>
  </header>

  <div class="mb-6">
    <Hero
      result={defaultCardResult}
      {selectedCards}
      {pinPickerOpen}
      {exporting}
      dataReviewedAt={latestReview}
      onTogglePin={() => (pinPickerOpen = !pinPickerOpen)}
      onPin={handlePin}
      onUnpin={handleUnpin}
      onExport={exportAsImage}
    />
  </div>

  <div class="space-y-3">
    {#each buckets as bucket}
      {#if bucket.def.id !== "daily"}
        <BucketSection
          {bucket}
          expanded={!!expanded[bucket.def.id]}
          pendingExpanded={!!pendingExpanded[bucket.def.id]}
          {selectedCards}
          {feedbackMap}
          onToggle={() => toggle(bucket.def.id)}
          onTogglePending={() => togglePending(bucket.def.id)}
          onFeedback={handleFeedback}
          {onEditProfile}
        />
      {/if}
    {/each}
  </div>

  <div class="mt-6">
    <CardsManager
      {selectedCards}
      {allCatalog}
      {customCards}
      onView={(c) => (detailCard = c)}
      onChange={(next) => {
        const beforeIds = selectedCards.map(c => c.id);
        const beforeCustom = customCards;
        const afterIds = new Set(next.selectedIds);
        const added = next.selectedIds.filter(x => !beforeIds.includes(x));
        const removed = beforeIds.filter(x => !afterIds.has(x));
        onCardsChange(next);
        if (added.length === 1) {
          const card = allCatalog.find(c => c.id === added[0]);
          toast.success(`已加入：${card?.shortName || card?.name || added[0]}`);
        } else if (added.length > 1) {
          toast.success(`已加入 ${added.length} 张卡`);
        }
        if (removed.length === 1) {
          const card = selectedCards.find(c => c.id === removed[0]);
          toast.info(`已移除：${card?.shortName || card?.name || removed[0]}`, {
            action: {
              label: "撤回",
              run: () => {
                onCardsChange({ selectedIds: beforeIds, customCards: beforeCustom });
                toast.success("已恢复");
              }
            }
          });
        } else if (removed.length > 1) {
          toast.info(`已移除 ${removed.length} 张卡`, {
            action: {
              label: "撤回",
              run: () => {
                onCardsChange({ selectedIds: beforeIds, customCards: beforeCustom });
                toast.success("已恢复");
              }
            }
          });
        }
      }}
    />
  </div>

  <footer class="mt-10 border-t border-dashed border-foreground/10 pt-6 text-center text-[11px] text-muted-foreground/80">
    <p class="tracking-wide">魔法小卡 · 你少做一个决策 ✦</p>
  </footer>
</div>

<!-- off-screen share target -->
<div
  bind:this={shareNode}
  aria-hidden="true"
  style="position: fixed; top: 0; left: -10000px; pointer-events: none;"
>
  <ShareableCard result={defaultCardResult} />
</div>

<CardDetail
  card={detailCard}
  {allBenefits}
  {profile}
  onClose={() => (detailCard = null)}
  onRemove={() => {
    const card = detailCard;
    if (!card) return;
    const beforeIds = selectedCards.map(c => c.id);
    const beforeCustom = customCards;
    detailCard = null;
    onCardsChange({
      selectedIds: beforeIds.filter(id => id !== card.id),
      customCards: customCards.filter(c => c.id !== card.id)
    });
    toast.info(`已移除：${card.shortName || card.name}`, {
      action: {
        label: "撤回",
        run: () => {
          onCardsChange({ selectedIds: beforeIds, customCards: beforeCustom });
          toast.success("已恢复");
        }
      }
    });
  }}
/>

<HelpSheet open={helpOpen} onClose={() => (helpOpen = false)} />

<MyReports
  open={reportsOpen}
  onClose={() => (reportsOpen = false)}
  {allBenefits}
  {allCatalog}
/>
